import { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { BeadTechnique } from '@bransoletki/shared';
import { BEAD_DIMENSIONS } from '@bransoletki/shared';
import { useEditorStore } from '@/stores/editor-store';
import type { EditorPaletteColor } from '@/stores/editor-store';
import { useContainerSize } from '@/hooks/use-container-size';
import { useEditorShortcuts } from '@/hooks/use-editor-shortcuts';
import { useAutosave } from '@/hooks/use-autosave';
import { fetchPattern } from '@/lib/api';
import { BeadGrid, calculateFitZoom } from '@/components/editor/bead-grid';
import { Toolbar } from '@/components/editor/toolbar';
import { ColorPalette } from '@/components/editor/color-palette';
import { BeadBrowser } from '@/components/editor/bead-browser';
import { BeadStats } from '@/components/editor/bead-stats';
import { NewProjectDialog } from '@/components/editor/new-project-dialog';
import { ImportWizard } from '@/components/import/import-wizard';
import { calculateGridSize, createRenderConfig } from '@/lib/grid-renderer';
import type { PipelineResult } from '@/lib/image-import';

const TECHNIQUES: readonly { value: BeadTechnique; label: string }[] = [
  { value: 'loom', label: 'Loom' },
  { value: 'peyote', label: 'Peyote' },
  { value: 'brick', label: 'Brick' },
] as const;

export function EditorPage() {
  const { id, slug } = useParams<{ id: string; slug: string }>();
  const navigate = useNavigate();
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const containerSize = useContainerSize(canvasContainerRef);
  const [isBrowserOpen, setIsBrowserOpen] = useState(false);
  const [isStatsOpen, setIsStatsOpen] = useState(!!slug);
  const [isNewProjectOpen, setIsNewProjectOpen] = useState(id === 'new');
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isPatternLoading, setIsPatternLoading] = useState(false);
  const [needsFit, setNeedsFit] = useState(false);

  useEditorShortcuts();
  useAutosave();

  // Load pattern from library when navigating via /editor/pattern/:slug
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setIsPatternLoading(true);

    fetchPattern(slug).then((res) => {
      if (cancelled || !res.data) return;
      const { name, technique, width, height, gridData } = res.data;

      const palette: EditorPaletteColor[] = gridData.palette.map((p, i) => ({
        index: i + 1,
        beadId: '',
        beadCode: p.label,
        beadHex: p.hex,
        label: p.label,
        count: 0,
      }));

      const store = useEditorStore.getState();
      store.setProjectName(name);
      store.setTechnique(technique as BeadTechnique);
      store.initGrid(width, height);
      store.setGrid(gridData.grid);
      store.setPalette(palette);
      setIsPatternLoading(false);
      setNeedsFit(true);
    }).catch(() => {
      setIsPatternLoading(false);
    });

    return () => { cancelled = true; };
  }, [slug]);

  const technique = useEditorStore((s) => s.technique);
  const setTechnique = useEditorStore((s) => s.setTechnique);
  const showGrid = useEditorStore((s) => s.showGrid);
  const toggleGrid = useEditorStore((s) => s.toggleGrid);
  const previewMode = useEditorStore((s) => s.previewMode);
  const togglePreview = useEditorStore((s) => s.togglePreview);
  const gridWidth = useEditorStore((s) => s.gridWidth);
  const gridHeight = useEditorStore((s) => s.gridHeight);
  const zoom = useEditorStore((s) => s.zoom);
  const panX = useEditorStore((s) => s.panX);
  const panY = useEditorStore((s) => s.panY);
  const setZoom = useEditorStore((s) => s.setZoom);
  const setPan = useEditorStore((s) => s.setPan);
  const setProjectName = useEditorStore((s) => s.setProjectName);
  const initGrid = useEditorStore((s) => s.initGrid);
  const setGrid = useEditorStore((s) => s.setGrid);
  const setPalette = useEditorStore((s) => s.setPalette);
  const projectName = useEditorStore((s) => s.projectName);
  const undo = useEditorStore((s) => s.undo);
  const redo = useEditorStore((s) => s.redo);

  const handleImportApply = useCallback(
    (result: PipelineResult) => {
      initGrid(result.width, result.height);
      setGrid(result.grid);
      setPalette(result.palette);
      setIsImportOpen(false);
    },
    [initGrid, setGrid, setPalette],
  );

  const handleFitToScreen = useCallback(() => {
    const config = createRenderConfig(technique, gridWidth, gridHeight);
    const size = calculateGridSize(config);
    const fitZoom = calculateFitZoom(
      containerSize.width,
      containerSize.height,
      size.width,
      size.height,
    );
    setZoom(fitZoom);
    setPan(0, 0);
  }, [technique, gridWidth, gridHeight, containerSize, setZoom, setPan]);

  // Auto fit-to-screen after pattern loads and container is sized
  useEffect(() => {
    if (!needsFit || containerSize.width === 0) return;
    // Read fresh values from store to avoid stale closure
    const s = useEditorStore.getState();
    const config = createRenderConfig(s.technique, s.gridWidth, s.gridHeight);
    const size = calculateGridSize(config);
    const fitZoom = calculateFitZoom(
      containerSize.width,
      containerSize.height,
      size.width,
      size.height,
    );
    s.setZoom(fitZoom);
    s.setPan(0, 0);
    setNeedsFit(false);
  }, [needsFit, containerSize]);

  const setBeadSize = useEditorStore((s) => s.setBeadSize);

  const handleCreateProject = useCallback(
    (config: { name: string; technique: BeadTechnique; width: number; height: number; beadSize: string }) => {
      setProjectName(config.name);
      setTechnique(config.technique);
      setBeadSize(config.beadSize);
      initGrid(config.width, config.height);
      setIsNewProjectOpen(false);
      navigate('/editor/new', { replace: true });
    },
    [setProjectName, setTechnique, setBeadSize, initGrid, navigate],
  );

  return (
    <div className="editor-viewport flex h-dvh flex-col">
      {/* Editor top bar */}
      <header className="flex h-11 items-center justify-between border-b border-gray-200 bg-white px-4 pt-[var(--sat)]">
        <button onClick={() => navigate(-1)} className="text-sm text-primary-600">
          ← Wróć
        </button>
        <span className="max-w-[40%] truncate text-sm font-medium text-gray-700">
          {projectName}
        </span>
        <div className="flex gap-2">
          <button
            onClick={() => setIsImportOpen(true)}
            className="text-xs font-medium text-gray-400 hover:text-primary-600"
          >
            Import
          </button>
          <button
            onClick={() => setIsStatsOpen((v) => !v)}
            className={`text-xs font-medium ${isStatsOpen ? 'text-primary-600' : 'text-gray-400'}`}
          >
            Statystyki
          </button>
        </div>
      </header>

      {/* Main area: canvas + optional stats sidebar */}
      <div className="flex flex-1 overflow-hidden">
        {/* Canvas + nav arrows */}
        <div className="relative flex-1 overflow-hidden bg-gray-100">
          <div ref={canvasContainerRef} className="h-full w-full">
            {isPatternLoading ? (
              <div className="flex h-full items-center justify-center">
                <p className="text-sm text-gray-400">Ładuję wzór...</p>
              </div>
            ) : containerSize.width > 0 && containerSize.height > 0 ? (
              <BeadGrid width={containerSize.width} height={containerSize.height} />
            ) : null}
          </div>

          {/* Vertical scroll arrows */}
          <div className="absolute right-2 top-1/2 z-10 flex -translate-y-1/2 flex-col gap-1">
            <button
              onClick={() => setPan(panX, panY + 80)}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-white/80 text-gray-600 shadow backdrop-blur-sm active:bg-gray-200"
              title="Przewiń w górę"
            >
              ▲
            </button>
            <button
              onClick={() => setPan(panX, panY - 80)}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-white/80 text-gray-600 shadow backdrop-blur-sm active:bg-gray-200"
              title="Przewiń w dół"
            >
              ▼
            </button>
          </div>
        </div>

        {/* Stats sidebar (desktop) */}
        {isStatsOpen && (
          <div className="hidden w-60 shrink-0 overflow-y-auto border-l border-gray-200 bg-white sm:block">
            <BeadStats />
          </div>
        )}
      </div>

      {/* Tools toolbar */}
      <div className="flex h-12 items-center justify-between border-t border-gray-200 bg-white px-3">
        <Toolbar />
        <div className="flex items-center gap-2">
          {/* Undo/Redo */}
          <button onClick={undo} className="rounded px-1.5 py-0.5 text-xs text-gray-500 hover:bg-gray-100" title="Cofnij (Ctrl+Z)">↩</button>
          <button onClick={redo} className="rounded px-1.5 py-0.5 text-xs text-gray-500 hover:bg-gray-100" title="Ponów (Ctrl+Y)">↪</button>
          <span className="hidden text-xs text-gray-400 sm:block">
            {Math.round(zoom * 100)}%
          </span>
        </div>
      </div>

      {/* Dimensions bar */}
      <DimensionsBar />

      {/* Color palette bar */}
      <div className="flex h-14 items-center border-t border-gray-200 bg-white px-3">
        <ColorPalette onOpenBrowser={() => setIsBrowserOpen(true)} />
      </div>

      {/* Bottom bar: technique + view controls */}
      <div className="flex h-11 items-center justify-between border-t border-gray-200 bg-white px-3 pb-[var(--sab)]">
        <div className="flex gap-1">
          {TECHNIQUES.map((t) => (
            <button
              key={t.value}
              onClick={() => setTechnique(t.value)}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                technique === t.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          <button
            onClick={handleFitToScreen}
            className="rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-200"
          >
            Dopasuj
          </button>
          <button
            onClick={toggleGrid}
            className={`rounded-md px-2 py-1 text-xs font-medium ${
              showGrid && !previewMode ? 'bg-gray-200 text-gray-700' : 'bg-gray-100 text-gray-400'
            }`}
            disabled={previewMode}
          >
            Siatka
          </button>
          <button
            onClick={togglePreview}
            className={`rounded-md px-2 py-1 text-xs font-medium ${
              previewMode
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Podgląd
          </button>
        </div>
      </div>

      {/* Modals */}
      <BeadBrowser isOpen={isBrowserOpen} onClose={() => setIsBrowserOpen(false)} />
      <NewProjectDialog
        isOpen={isNewProjectOpen}
        onClose={() => setIsNewProjectOpen(false)}
        onCreate={handleCreateProject}
      />
      <ImportWizard
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        onApply={handleImportApply}
      />

      {/* Stats bottom sheet (mobile) */}
      {isStatsOpen && (
        <div className="fixed inset-0 z-40 flex flex-col justify-end sm:hidden">
          <div className="flex-1 bg-black/30" onClick={() => setIsStatsOpen(false)} />
          <div className="max-h-[60dvh] overflow-y-auto rounded-t-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between px-4 pt-3">
              <h3 className="text-sm font-semibold text-gray-900">Statystyki</h3>
              <button onClick={() => setIsStatsOpen(false)} className="text-xs text-gray-400">Zamknij</button>
            </div>
            <BeadStats />
          </div>
        </div>
      )}
    </div>
  );
}

/** Compact dimensions bar: width (beads) × length (cm) with live mm preview */
function DimensionsBar() {
  const gridWidth = useEditorStore((s) => s.gridWidth);
  const gridHeight = useEditorStore((s) => s.gridHeight);
  const grid = useEditorStore((s) => s.grid);
  const palette = useEditorStore((s) => s.palette);
  const beadSize = useEditorStore((s) => s.beadSize);
  const resizeGrid = useEditorStore((s) => s.resizeGrid);

  const dims = BEAD_DIMENSIONS[beadSize] ?? [1.6, 1.3];
  const beadH = dims[1];

  const lengthMm = Math.round(gridHeight * beadH * 10) / 10;
  const lengthCm = Math.round(lengthMm / 10 * 10) / 10;

  // Count beads per color (reactive to grid changes)
  const beadCounts = useMemo(() => {
    const counts = new Map<number, number>();
    let total = 0;
    for (const row of grid) {
      for (const cell of row) {
        if (cell > 0) {
          total++;
          counts.set(cell, (counts.get(cell) ?? 0) + 1);
        }
      }
    }
    return { total, counts };
  }, [grid]);

  const [editWidth, setEditWidth] = useState<string>('');
  const [editCm, setEditCm] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);

  const handleApply = useCallback(() => {
    const newWidth = editWidth ? Math.max(1, Math.min(500, parseInt(editWidth, 10) || gridWidth)) : gridWidth;
    const newCm = editCm ? parseFloat(editCm) || lengthCm : lengthCm;
    const newHeight = Math.max(1, Math.round((newCm * 10) / beadH));
    if (newWidth !== gridWidth || newHeight !== gridHeight) {
      resizeGrid(newWidth, newHeight);
    }
    setIsEditing(false);
  }, [editWidth, editCm, gridWidth, gridHeight, lengthCm, beadH, resizeGrid]);

  if (isEditing) {
    return (
      <div className="flex h-10 items-center gap-2 border-t border-gray-200 bg-gray-50 px-3">
        <label className="flex items-center gap-1 text-[10px] text-gray-500">
          Szer.
          <input
            type="number"
            defaultValue={gridWidth}
            onChange={(e) => setEditWidth(e.target.value)}
            className="selectable w-12 rounded border border-gray-300 px-1 py-0.5 text-xs"
            min={1} max={500}
          />
        </label>
        <label className="flex items-center gap-1 text-[10px] text-gray-500">
          Dł. (cm)
          <input
            type="number"
            defaultValue={lengthCm}
            onChange={(e) => setEditCm(e.target.value)}
            step={0.5} min={0.5} max={100}
            className="selectable w-14 rounded border border-gray-300 px-1 py-0.5 text-xs"
          />
        </label>
        <button onClick={handleApply} className="rounded bg-primary-600 px-2 py-0.5 text-[10px] font-medium text-white">
          OK
        </button>
        <button onClick={() => setIsEditing(false)} className="text-[10px] text-gray-400">
          Anuluj
        </button>
      </div>
    );
  }

  return (
    <div className="border-t border-gray-200 bg-gray-50">
      <button
        onClick={() => setIsEditing(true)}
        className="flex h-8 w-full items-center gap-2 px-3 text-[11px] text-gray-500 active:bg-gray-100"
      >
        <span>{gridWidth} × {gridHeight}</span>
        <span className="text-gray-300">|</span>
        <span>≈ {lengthCm} cm</span>
        <span className="text-gray-300">|</span>
        <span>{beadSize}</span>
        <span className="text-gray-300">|</span>
        <span className="font-medium">{beadCounts.total} szt.</span>
        <span className="ml-auto text-[10px] text-primary-500">Zmień</span>
      </button>
      {/* Per-color bead counts */}
      {palette.length > 0 && (
        <div className="flex gap-1.5 overflow-x-auto px-3 pb-1.5">
          {palette.map((color) => {
            const count = beadCounts.counts.get(color.index) ?? 0;
            if (count === 0) return null;
            return (
              <div key={color.index} className="flex shrink-0 items-center gap-1 rounded bg-white px-1.5 py-0.5 shadow-sm">
                <div className="h-3 w-3 rounded-sm border border-gray-200" style={{ backgroundColor: color.beadHex }} />
                <span className="text-[10px] font-medium text-gray-600">{color.label}</span>
                <span className="text-[10px] text-gray-400">{count}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
